import { useEffect, useRef, useState } from 'react';

/*
 * Normalizes URLs so query params always appear inside the hash fragment.
 * This prevents broken routing when OAuth or external redirects append
 * query params before the hash in hash-based routing setups.
 */
export function useNormalizeHashUrl(auth) {
  const lastProcessedUrlRef = useRef('');
  const [currentHash, setCurrentHash] = useState(window.location.hash);

  // Track hash changes
  useEffect(() => {
    const handleHashChange = () => {
      setCurrentHash(window.location.hash);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Normalize URL after auth is settled
  useEffect(() => {
    if (!auth?.isAuthenticated || auth?.isLoading) return;

    const currentHref = window.location.href;

    // Prevent infinite loops
    if (lastProcessedUrlRef.current === currentHref) return;

    const url = new URL(currentHref);

    // If query params exist BEFORE the hash
    if (url.search && url.search.length > 1) {
      const hashMatch = currentHref.match(/#(.+)$/);
      const hashRoute = hashMatch
        ? hashMatch[1].split('?')[0]
        : '/';

      const queryParams = [];
      url.searchParams.forEach((value, key) => {
        queryParams.push(`${key}=${value}`);
      });

      const pathname = window.location.pathname || '/';
      const hashWithParams = `#${hashRoute}?${queryParams.join('&')}`;
      const reorderedUrl = `${url.origin}${pathname}${hashWithParams}`;

      if (currentHref !== reorderedUrl) {
        lastProcessedUrlRef.current = reorderedUrl;
        window.history.replaceState(null, '', reorderedUrl);

        // replaceState does not trigger hashchange
        setCurrentHash(window.location.hash);
      } else {
        lastProcessedUrlRef.current = currentHref;
      }
    } else {
      lastProcessedUrlRef.current = currentHref;
    }
  }, [currentHash, auth?.isAuthenticated, auth?.isLoading]);
}
